'use client'

import { useState, memo } from 'react'
import { NotebookIcon } from '@phosphor-icons/react'
import { Input } from '@/ui/input'
import { Textarea } from '@/ui/textarea'
import { MultiSelectProperty, PROPERTY_ICONS } from '@/ui/property-fields'
import { TopicSubtitleSelector } from '@/capture'
import { AddTopicDialog, useFindOrCreateTopic, useTopics } from '@/topics'
// Same read again. Step 2 added this edge too.
// eslint-disable-next-line import/no-restricted-paths
import { usePeople } from '@/legacy/supabase-store/use-people'
import { EditFormActions } from '@/feed/row-chrome'
import { DeleteButton } from '@/ui/delete-button'
import { EditButton } from '@/ui/edit-button'
import { SourceLink } from '@/feed/row-chrome'
import { formatRelativeTime } from '@/lib/date'
import { MarkdownContent } from '@/ui/markdown-content'
import { PromptBadge } from './prompt-badge'
import type { PromptCategory } from './meta'
// The shared feed chrome, through `@/feed`'s face. This import closes a cycle —
// `@/feed` -> `@/entries` -> this file -> `@/feed` — which holds only while every cross-face read
// stays inside a function body (ADR 0014:108-112). Do not hoist one to module scope.
import {
  ExpandableRow,
  RowHeader,
  RowActions,
  MetaTagsRow,
  TopicBadges,
} from '@/feed/row-chrome'
import type { PersonRef } from '@/document'
import type { RowProps } from '../types'
import type { JournalEntry } from './meta'
import type { JournalDraft } from './write-spec'

/**
 * One interface, not seven (#359). `availablePeople` and `availableTopics` were this row's alone —
 * no media row carried them — so they leave the props rather than being widened into the shared
 * one. Both are document reads, and the row makes them itself.
 */
type JournalRowProps = RowProps<JournalEntry, JournalDraft>

export const JournalRow = memo(
  function JournalRow({
    entry,
    isExpanded,
    onToggle,
    onDelete,
    onSave,
    isDeleting,
    isSaving,
  }: JournalRowProps) {
    // `useTopics('journal')` is byte-for-byte the list `unified-feed.tsx` passed down: the same
    // archived filter and the same sort, with the kind filter applied inside instead of after.
    const { topics: availableTopics } = useTopics('journal')
    const { data: availablePeople = [] } = usePeople()
    const [isEditing, setIsEditing] = useState(false)
    const [editTitle, setEditTitle] = useState(entry.title)
    const [editContent, setEditContent] = useState(entry.content || '')
    const [editPeopleIds, setEditPeopleIds] = useState<string[]>(
      entry.people?.map((p) => p.id) || [],
    )
    const [editTopicIds, setEditTopicIds] = useState<string[]>(
      entry.topicIds || [],
    )

    const findOrCreateTopic = useFindOrCreateTopic()
    const people = entry.people || []
    const topics = entry.topics || []

    // Inline meta editing handlers (view mode)
    const viewPeople = availablePeople.filter((p) =>
      people.some((ep) => ep.id === p.id),
    )

    const handleInlineTopicsChange = (topicIds: string[]) => {
      onSave({ topicIds })
    }

    const handleInlinePeopleAdd = (person: PersonRef) => {
      const currentIds = people.map((p) => p.id)
      onSave({ peopleIds: [...currentIds, person.id] })
    }

    const handleInlinePeopleRemove = (person: PersonRef) => {
      const currentIds = people
        .map((p) => p.id)
        .filter((id) => id !== person.id)
      onSave({ peopleIds: currentIds })
    }

    const handleStartEdit = (e: React.MouseEvent) => {
      e.stopPropagation()
      setIsEditing(true)
      setEditTitle(entry.title)
      setEditContent(entry.content || '')
      setEditPeopleIds(entry.people?.map((p) => p.id) || [])
      setEditTopicIds(entry.topicIds || [])
    }

    const handleCancelEdit = () => {
      setIsEditing(false)
    }

    const handleSave = async () => {
      await onSave({
        title: editTitle,
        content: editContent,
        peopleIds: editPeopleIds,
        topicIds: editTopicIds,
      })
      setIsEditing(false)
    }

    const selectedPeople = availablePeople.filter((p) =>
      editPeopleIds.includes(p.id),
    )

    const header = (
      <RowHeader
        onToggle={onToggle}
        isExpanded={isExpanded}
        icon={
          <NotebookIcon
            className="text-entry-journal h-5 w-5 sm:h-4 sm:w-4"
            weight="duotone"
          />
        }
        title={entry.title}
        titleSuffix={
          topics.length > 0 && (
            <span className="hidden sm:inline">
              <TopicBadges topics={topics} maxDisplay={2} />
            </span>
          )
        }
        metadata={
          <span className="hidden items-center gap-2 sm:inline-flex">
            {typeof entry.metadata.promptCategory === 'string' ? (
              <PromptBadge
                category={entry.metadata.promptCategory as PromptCategory}
              />
            ) : null}
            <span className="text-muted-foreground/50 text-xs">
              {formatRelativeTime(entry.createdAt)}
            </span>
          </span>
        }
      />
    )

    return (
      <ExpandableRow
        isExpanded={isExpanded}
        entryType="journal"
        header={header}
      >
        <div className="px-2 pt-1 pb-2 sm:pr-12 sm:pb-4 sm:pl-12">
          {isEditing ? (
            <JournalEditForm
              editTitle={editTitle}
              setEditTitle={setEditTitle}
              editContent={editContent}
              setEditContent={setEditContent}
              editPeopleIds={editPeopleIds}
              setEditPeopleIds={setEditPeopleIds}
              editTopicIds={editTopicIds}
              setEditTopicIds={setEditTopicIds}
              selectedPeople={selectedPeople}
              availablePeople={availablePeople}
              availableTopics={availableTopics}
              isSaving={isSaving}
              onCancel={handleCancelEdit}
              onSave={handleSave}
            />
          ) : (
            <>
              {entry.metadata.promptText && (
                <p className="text-muted-foreground/40 mb-2 text-xs italic">
                  You wrote this in response to:{' '}
                  {String(entry.metadata.promptText)}
                </p>
              )}
              {entry.content && (
                <MarkdownContent className="font-body-serif text-foreground/80 mb-3 text-sm leading-relaxed">
                  {entry.content}
                </MarkdownContent>
              )}

              {/* Inline meta tags row */}
              <MetaTagsRow className="mb-3">
                <TopicSubtitleSelector
                  selectedTopicIds={entry.topicIds || []}
                  availableTopics={availableTopics}
                  onChange={handleInlineTopicsChange}
                  onAddNew={() => {}}
                  onCreateTopic={(name) => {
                    const topicId = findOrCreateTopic(name, 'journal')
                    if (topicId) {
                      handleInlineTopicsChange([
                        ...(entry.topicIds || []),
                        topicId,
                      ])
                    }
                  }}
                  disabled={isSaving}
                />

                <MultiSelectProperty
                  label="People"
                  Icon={PROPERTY_ICONS.people}
                  selectedItems={viewPeople}
                  availableItems={availablePeople}
                  onAdd={handleInlinePeopleAdd}
                  onRemove={handleInlinePeopleRemove}
                  getItemLabel={(p) => p.name}
                  disabled={isSaving}
                />
              </MetaTagsRow>

              <div className="flex items-center gap-3">
                <SourceLink entry={entry} />
                <div className="flex-1" />
                <RowActions className="ml-auto">
                  <EditButton
                    onClick={handleStartEdit}
                    variant="icon"
                    className="opacity-100 sm:opacity-0 sm:transition-opacity sm:group-hover:opacity-100"
                  />
                  <DeleteButton
                    onClick={onDelete}
                    isDeleting={isDeleting}
                    variant="icon"
                    className="opacity-100 sm:opacity-0 sm:transition-opacity sm:group-hover:opacity-100"
                  />
                </RowActions>
              </div>
            </>
          )}
        </div>
      </ExpandableRow>
    )
  },
  // Memoization comparison
  (prevProps, nextProps) => {
    return (
      prevProps.entry.id === nextProps.entry.id &&
      prevProps.entry.title === nextProps.entry.title &&
      prevProps.entry.content === nextProps.entry.content &&
      prevProps.isExpanded === nextProps.isExpanded &&
      prevProps.isDeleting === nextProps.isDeleting &&
      prevProps.isSaving === nextProps.isSaving &&
      JSON.stringify(prevProps.entry.topics) ===
        JSON.stringify(nextProps.entry.topics) &&
      JSON.stringify(prevProps.entry.people) ===
        JSON.stringify(nextProps.entry.people) &&
      prevProps.entry.url === nextProps.entry.url
    )
  },
)

interface JournalEditFormProps {
  editTitle: string
  setEditTitle: (v: string) => void
  editContent: string
  setEditContent: (v: string) => void
  editPeopleIds: string[]
  setEditPeopleIds: (v: string[]) => void
  editTopicIds: string[]
  setEditTopicIds: (v: string[]) => void
  selectedPeople: PersonRef[]
  availablePeople: PersonRef[]
  availableTopics: Array<{ id: string; name: string }>
  isSaving: boolean
  onCancel: () => void
  onSave: () => void
}

function JournalEditForm({
  editTitle,
  setEditTitle,
  editContent,
  setEditContent,
  editPeopleIds,
  setEditPeopleIds,
  editTopicIds,
  setEditTopicIds,
  selectedPeople,
  availablePeople,
  availableTopics,
  isSaving,
  onCancel,
  onSave,
}: JournalEditFormProps) {
  const [showAddTopic, setShowAddTopic] = useState(false)
  const findOrCreateTopic = useFindOrCreateTopic()

  const handleCreateTopic = (name: string) => {
    const topicId = findOrCreateTopic(name, 'journal')
    if (topicId && !editTopicIds.includes(topicId)) {
      setEditTopicIds([...editTopicIds, topicId])
    }
  }

  return (
    <div className="space-y-3">
      <div>
        <label className="text-muted-foreground mb-1 block text-xs">
          Title
        </label>
        <Input
          value={editTitle}
          onChange={(e) => setEditTitle(e.target.value)}
          className="h-8 text-sm"
          placeholder="Journal title"
        />
      </div>

      <div>
        <label className="text-muted-foreground mb-1 block text-xs">
          Content
        </label>
        <Textarea
          value={editContent}
          onChange={(e) => setEditContent(e.target.value)}
          className="min-h-[100px] resize-none text-sm"
          placeholder="Write your thoughts..."
        />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <TopicSubtitleSelector
          selectedTopicIds={editTopicIds}
          availableTopics={availableTopics}
          onChange={setEditTopicIds}
          onAddNew={() => setShowAddTopic(true)}
          onCreateTopic={handleCreateTopic}
          disabled={isSaving}
        />

        <MultiSelectProperty
          label="People"
          Icon={PROPERTY_ICONS.people}
          selectedItems={selectedPeople}
          availableItems={availablePeople}
          onAdd={(person) => setEditPeopleIds([...editPeopleIds, person.id])}
          onRemove={(person) =>
            setEditPeopleIds(editPeopleIds.filter((id) => id !== person.id))
          }
          getItemLabel={(p) => p.name}
        />
      </div>

      <EditFormActions
        isSaving={isSaving}
        onCancel={onCancel}
        onSave={onSave}
        disabled={!editTitle.trim() || !editContent.trim()}
      />

      <AddTopicDialog
        open={showAddTopic}
        onOpenChange={setShowAddTopic}
        onCreate={handleCreateTopic}
      />
    </div>
  )
}
